import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  UserGroupIcon,
  FireIcon,
  Cog6ToothIcon,
  Bars3Icon,
  XMarkIcon,
  BellIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import {
  HomeIcon as HomeSolid,
  UserGroupIcon as UserGroupSolid,
  FireIcon as FireSolid,
  Cog6ToothIcon as CogSolid,
} from '@heroicons/react/24/solid';
import { useAuth } from '../../contexts/AuthContext';
import { useWebSocket } from '../../contexts/WebSocketContext';
import clsx from 'clsx';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon, iconSolid: HomeSolid },
  { name: 'Leads',     href: '/leads',     icon: UserGroupIcon, iconSolid: UserGroupSolid },
  { name: 'Hype',      href: '/hype',      icon: FireIcon, iconSolid: FireSolid },
  { name: 'Settings',  href: '/settings',  icon: Cog6ToothIcon, iconSolid: CogSolid },
];

export default function DashboardLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();
  const { notifications } = useWebSocket();
  const unread = notifications.filter(n => !n.read).length;

  const currentPage = navigation.find(n =>
    location.pathname === n.href || location.pathname.startsWith(n.href + '/')
  )?.name ?? '';

  const Sidebar = () => (
    <div className="flex flex-col h-full bg-white border-r border-gray-100">
      {/* Logo */}
      <div className="px-5 pt-6 pb-5">
        <div className="flex items-center gap-3">
          <img
            src="/favicon.svg"
            alt="198X"
            className="w-9 h-9 rounded-xl object-cover flex-shrink-0"
          />
          <div>
            <p className="text-sm font-bold text-gray-900 leading-tight">{user?.companyName ?? 'estaie'}</p>
            <p className="text-xs text-gray-400 leading-tight">Powered by 198X</p>
          </div>
        </div>
      </div>

      <div className="px-3 mb-1">
        <p className="px-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1">Menu</p>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 space-y-0.5">
        {navigation.map(item => {
          const active = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
          const Icon = active ? item.iconSolid : item.icon;
          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={() => setMobileOpen(false)}
              className={clsx(
                'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150',
                active
                  ? 'bg-blue-50 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              )}
            >
              <Icon className={clsx('h-5 w-5 flex-shrink-0', active ? 'text-blue-600' : 'text-gray-400')} />
              {item.name}
              {active && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-blue-600" />}
            </Link>
          );
        })}
      </nav>

      {/* User */}
      <div className="mx-3 mb-4 mt-3 p-3 rounded-xl bg-gray-50 border border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
            <span className="text-white text-xs font-bold uppercase">{user?.email?.[0] ?? 'U'}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-gray-800 truncate">{user?.email}</p>
            <p className="text-xs text-gray-400 capitalize">{user?.role ?? 'Admin'}</p>
          </div>
          <button
            onClick={logout}
            title="Sign out"
            className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors flex-shrink-0"
          >
            <ArrowRightOnRectangleIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen appdark flex">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-64 shadow-2xl z-10">
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute top-4 right-4 p-1.5 rounded-lg bg-white/10 text-gray-400 hover:text-gray-600 z-20"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
            <Sidebar />
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:w-56 lg:flex-col lg:fixed lg:inset-y-0 shadow-sm">
        <Sidebar />
      </div>

      {/* Main */}
      <div className="lg:pl-56 flex flex-col flex-1 min-w-0">
        {/* Header */}
        <header className="sticky top-0 z-10 bg-white border-b border-gray-100 h-14 flex items-center px-5 gap-4">
          <button
            className="lg:hidden p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
            onClick={() => setMobileOpen(true)}
          >
            <Bars3Icon className="h-5 w-5" />
          </button>

          <div className="flex-1" />

          <div className="flex items-center gap-1">
            <button className="relative p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
              <BellIcon className="h-5 w-5" />
              {unread > 0 && (
                <span className="absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-full bg-red-500" />
              )}
            </button>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-6">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
